/**
 * Custom gRPC Transport Configuration
 *
 * This configures the gRPC-Web transport with:
 * - Environment-based URL detection
 * - Authentication interceptor
 * - Logging interceptor (dev only)
 */

import {
  type RpcInterceptor,
  createAuthInterceptor,
  createHeadersInterceptor,
  createLoggingInterceptor,
  createStreamingTransport,
  createTransport,
  getDefaultTransport,
  setDefaultTransport,
} from "@fermata/grpc-zustand/runtime"
import type { RpcTransport } from "@protobuf-ts/runtime-rpc"
import { getGrpcEndpoint } from "./grpc-endpoint"

/**
 * Get authentication token from storage
 * TODO: Replace with your actual auth implementation
 */
function getAuthToken(): string | null {
  if (typeof window === "undefined") return null

  // Example: Get from localStorage
  return localStorage.getItem("auth_token")

  // Or from a cookie, auth context, etc.
}

/**
 * Create configured interceptors
 */
function createInterceptors(): RpcInterceptor[] {
  const interceptors: RpcInterceptor[] = []

  // Auth interceptor - always enabled
  interceptors.push(createAuthInterceptor(getAuthToken))

  // Custom headers - request ID, client version, etc.
  // Note: we ALSO add Authorization here because we know this interceptor is executing
  // (it's easy to spot in DevTools), and it avoids any edge cases where the transport
  // doesn't honor meta changes from a separate interceptor.
  interceptors.push(
    createHeadersInterceptor(() => {
      const headers: Record<string, string> = {
        "X-Client-Version": "1.0.0",
        "X-Request-ID": crypto.randomUUID(),
      }

      const token = getAuthToken()
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      return headers
    })
  )

  // Logging interceptor - only in development
  if (import.meta.env.DEV) {
    interceptors.push(createLoggingInterceptor())
  }

  return interceptors
}

/**
 * Initialize the custom transport
 * Call this once at app startup
 */
export function initializeGrpcTransport(): RpcTransport {
  const baseUrl = getGrpcEndpoint()

  console.log(`🔌 Initializing gRPC transport: ${baseUrl}`)

  const transport = createTransport({
    baseUrl,
    timeout: 30000,
    interceptors: createInterceptors(),
  })

  // Store transport for use by generated clients
  // Generated clients call getDefaultTransport(), so we must override the singleton.
  setDefaultTransport(transport)

  return transport
}

let streamTransport: RpcTransport | undefined

/**
 * Transport for long-lived server streams (StreamTelemetry).
 *
 * Points at VITE_GRPC_STREAM_ENDPOINT — a gRPC-Web ingress that bypasses REST
 * API Gateway (which buffers responses and caps integration at ~29s, 504-ing
 * long streams). When unset (local dev / preview), it falls back to the default
 * transport so nothing special is required to run locally. It uses a deadline-
 * free transport so the stream isn't cancelled mid-flight (see
 * createStreamingTransport). Auth is identical to the default transport (same
 * interceptors), so the bypass ingress is still bearer-authenticated per call.
 */
export function getStreamTransport(): RpcTransport {
  if (streamTransport) return streamTransport

  const streamUrl = import.meta.env.VITE_GRPC_STREAM_ENDPOINT
  if (!streamUrl) return getDefaultTransport()

  console.log(`🔌 Initializing gRPC streaming transport: ${streamUrl}`)
  streamTransport = createStreamingTransport({
    baseUrl: streamUrl,
    interceptors: createInterceptors(),
  })
  return streamTransport
}

// Auto-initialize on module load
initializeGrpcTransport()
